import type {
	CampaignConfigContentITem,
	CategoryConfigContentItem,
	Tersed,
	PageConfigContentItem,
} from '@/services/kontent-ai/types'
import type {ContainerClassName} from '@/products/flexible-web-toolkit/layout/layout-common-types'
import type {HeaderConfigContentItem} from '../components/header/models'
import {mainNavBorderColors} from '../components/main-nav/helper'
import type {MainNavBorderColor} from '../components/main-nav/helper'

/**
 * Get brand color from page config (new fields only).
 *
 * Supports:
 * - `brandColor`
 * - `snippetCategoryColorBrandColor`
 */
export function getBrandColorNew(
	config: Tersed<PageConfigContentItem | HeaderConfigContentItem>
): string | null {
	// New standardized brandColor field (all config types except Article)
	if ('brandColor' in config.elements) {
		const brandColorValue = config.elements.brandColor
		if (typeof brandColorValue === 'string' && brandColorValue) {
			return brandColorValue
		}
	}

	// Category config new brand color (from snippet)
	if ('snippetCategoryColorBrandColor' in config.elements) {
		const categoryConfig = config as Tersed<CategoryConfigContentItem>
		const brandColorValue =
			categoryConfig.elements.snippetCategoryColorBrandColor
		if (typeof brandColorValue === 'string' && brandColorValue) {
			return brandColorValue
		}
	}

	return null
}

/** Extract color from legacy `color` field (BrandConfig custom element or ProductConfig dropdown) */
function extractLegacyColor(elements: Record<string, unknown>): string | null {
	if (!('color' in elements)) return null
	const colorValue = elements.color
	if (typeof colorValue === 'string' && colorValue) return colorValue
	if (Array.isArray(colorValue) && colorValue.length > 0) {
		const firstItem = colorValue[0] as {codename?: string} | undefined
		return firstItem?.codename ?? null
	}
	return null
}

/** Extract color from legacy `colorScheme` field (CampaignConfig) */
function extractLegacyColorScheme(
	config: Tersed<PageConfigContentItem | HeaderConfigContentItem>
): string | null {
	if (!('colorScheme' in config.elements)) return null
	const campaignConfig = config as Tersed<CampaignConfigContentITem>
	const value = campaignConfig.elements.colorScheme
	return typeof value === 'string' && value ? value : null
}

/** Extract color from legacy `snippetCategoryColorCategoryColor` field */
function extractLegacyCategoryColor(
	config: Tersed<PageConfigContentItem | HeaderConfigContentItem>
): string | null {
	if (!('snippetCategoryColorCategoryColor' in config.elements)) return null
	const categoryConfig = config as Tersed<CategoryConfigContentItem>
	const value = categoryConfig.elements.snippetCategoryColorCategoryColor
	const codename = Array.isArray(value) ? value[0]?.codename : undefined
	return codename ?? null
}

/** Extract color from legacy `borderColor` field (HeaderConfig) */
function extractLegacyBorderColor(
	config: Tersed<PageConfigContentItem | HeaderConfigContentItem>
): string | null {
	if (!('borderColor' in config.elements)) return null
	const headerConfig = config as Tersed<HeaderConfigContentItem>
	const borderColorValue = headerConfig.elements.borderColor
	const codename = Array.isArray(borderColorValue)
		? borderColorValue[0]?.codename
		: undefined
	return codename && Object.keys(mainNavBorderColors).includes(codename)
		? (codename as MainNavBorderColor)
		: null
}

/**
 * Get brand color from page config (legacy fields only).
 *
 * Supports:
 * - `color`
 * - `colorScheme`
 * - `snippetCategoryColorCategoryColor`
 */
export function getBrandColorOld(
	config: Tersed<PageConfigContentItem | HeaderConfigContentItem>
): string | null {
	return (
		extractLegacyColor(config.elements as Record<string, unknown>) ??
		extractLegacyColorScheme(config) ??
		extractLegacyCategoryColor(config) ??
		extractLegacyBorderColor(config) ??
		null
	)
}

/**
 * Get brand color from page config with support for all config types.
 *
 * This function extracts the brand/theme color from various page configuration types.
 * It supports the PageConfigContentItem union which includes:
 * - BrandConfigContentItem: Uses `brandColor` (new) or `color` (deprecated)
 * - CampaignConfigContentITem: Uses `brandColor` (new) or `colorScheme` (deprecated)
 * - CategoryConfigContentItem: Uses `snippetCategoryColorBrandColor` (new) or
 *   `snippetCategoryColorCategoryColor` (deprecated)
 * - ProductConfigContentItem: Uses `brandColor` (new) or `color` (deprecated dropdown)
 * - ArticleConfigContentItem: No color field (returns null)
 *
 * @param config - Page config (Brand, Campaign, Category, Product, or Article config)
 * @returns Color codename string or null if no color is set
 */
export function getBrandColor(
	config: Tersed<PageConfigContentItem | HeaderConfigContentItem>
): string | null {
	return getBrandColorNew(config) ?? getBrandColorOld(config)
}

export const getContainerClass = (
	container: string,
	defaultContainer: ContainerClassName = 'container-wide'
): ContainerClassName => {
	switch (container) {
		case 'page': {
			return 'w-full'
		}
		case 'container': {
			return 'container'
		}
		case 'wide': {
			return 'container-wide'
		}
		case 'narrow': {
			return 'container-narrow'
		}
		default: {
			return defaultContainer
		}
	}
}
interface CustomBrand {
	background: string
	border: string
}
// eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style -- preferring index signature than a record
interface CustomBrandObject {
	[k: string]: CustomBrand
}

// Verbose object, creating these dynamically would be purged by Tailwind
/* Brands which have colors that are not a part of the Component Library's original set */

export const customBrands: CustomBrandObject = {
	elura: {
		background: 'bg-elura',
		border: 'border-elura',
	},
	entyce: {
		background: 'bg-entyce',
		border: 'border-entyce',
	},
	comfortis: {
		background: 'bg-comfortis',
		border: 'border-comfortis',
	},
	advocate: {
		background: 'bg-advocate',
		border: 'border-advocate',
	},
	experior_green: {
		background: 'bg-experior_green',
		border: 'border-experior_green',
	},
	zenrelia_primary: {
		background: 'bg-zenrelia_primary',
		border: 'border-zenrelia_primary',
	},
	zenrelia_secondary: {
		background: 'bg-zenrelia_secondary',
		border: 'border-zenrelia_secondary',
	},
	zenrelia_contrast: {
		background: 'bg-zenrelia_contrast',
		border: 'border-zenrelia_contrast',
	},
	zenrelia_accent: {
		background: 'bg-zenrelia_accent',
		border: 'border-zenrelia_accent',
	},
	zenrelia_sku1: {
		background: 'bg-zenrelia_sku1',
		border: 'border-zenrelia_sku1',
	},
	zenrelia_sku2: {
		background: 'bg-zenrelia_sku2',
		border: 'border-zenrelia_sku2',
	},
	zenrelia_sku3: {
		background: 'bg-zenrelia_sku3',
		border: 'border-zenrelia_sku3',
	},
	zenrelia_sku4: {
		background: 'bg-zenrelia_sku4',
		border: 'border-zenrelia_sku4',
	},
	credelio_quattro_guard_dog: {
		background: 'bg-credelio_quattro_guard_dog',
		border: 'border-credelio_quattro_guard_dog',
	},
	credelio_quattro_hunter_green: {
		background: 'bg-credelio_quattro_hunter_green',
		border: 'border-credelio_quattro_hunter_green',
	},
	credelio_quattro_grass_green: {
		background: 'bg-credelio_quattro_grass_green',
		border: 'border-credelio_quattro_grass_green',
	},
	credelio_quattro_tennis_ball_green: {
		background: 'bg-credelio_quattro_tennis_ball_green',
		border: 'border-credelio_quattro_tennis_ball_green',
	},
	credelio_quattro_park_green: {
		background: 'bg-credelio_quattro_park_green',
		border: 'border-credelio_quattro_park_green',
	},
	credelio_quattro_milk_bone: {
		background: 'bg-credelio_quattro_milk_bone',
		border: 'border-credelio_quattro_milk_bone',
	},

	credelio_quattro_golden: {
		background: 'bg-credelio_quattro_golden',
		border: 'border-credelio_quattro_golden',
	},
	credelio_quattro_frisbee: {
		background: 'bg-credelio_quattro_frisbee',
		border: 'border-credelio_quattro_frisbee',
	},
	credelio_quattro_sunny_day: {
		background: 'bg-credelio_quattro_sunny_day',
		border: 'border-credelio_quattro_sunny_day',
	},
	credelio_quattro_sky_blue: {
		background: 'bg-credelio_quattro_sky_blue',
		border: 'border-credelio_quattro_sky_blue',
	},
}

export const customBrandChoices: string[] = Object.keys(customBrands)
